import { Radio, RadioGroup } from '@billy/design-system'
import difference from 'lodash/difference'
import intersection from 'lodash/intersection'
import { ChangeEvent, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceLineColumn } from '@features/invoices/types/invoiceLineColumn'

import { useWatch } from '../../../../../../../../../../../../../../../../hooks'
import { useInvoiceColumnVisibilitySettingsForm } from '../../../../../../../../../../../../contexts/invoiceColumnVisibilityFormContext'
import { useInvoiceEditorForm } from '../../../../../../../../../../../../contexts/invoiceEditorFormContext'
import { useInvoiceLineHeaderLabels } from '../../../../../../../../hooks/useInvoiceLineHeaderLabels'
import { InvoiceColumnsVisibilitySettingsSection } from '../InvoiceColumnsVisibilitySettingsSection'

enum TotalPriceColumnSetting {
  GrossTotal = 'total',
  NetTotal = 'netTotal',
  Both = 'total,netTotal',
}
const totalPriceOptions: InvoiceLineColumn[] = [TotalPriceColumnSetting.GrossTotal, TotalPriceColumnSetting.NetTotal]

const getTotalPriceSettingValue = (selectedColumns: InvoiceLineColumn[]) => {
  const savedTotalPriceColumns = intersection(selectedColumns, totalPriceOptions)
  return savedTotalPriceColumns.length > 1 ? TotalPriceColumnSetting.Both : savedTotalPriceColumns[0]
}

export const TotalPriceColumnsSettings = () => {
  const { t } = useTranslation()
  const { getHeaderLabel } = useInvoiceLineHeaderLabels()
  const { invoiceLinesColumnsVisibilityForm, isLoading } = useInvoiceColumnVisibilitySettingsForm()
  const { control, setValue } = invoiceLinesColumnsVisibilityForm
  const { trackInvoiceEditor } = useInvoiceEditorForm()

  const visibleColumns = useWatch({ control, name: 'selectedColumns' })

  const value = getTotalPriceSettingValue(visibleColumns)

  const handleTotalPriceRadioClick = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selectedColumns = [
        ...difference([...visibleColumns], totalPriceOptions),
        ...event?.target?.value?.split(','),
      ]
      setValue('selectedColumns', selectedColumns, { shouldDirty: true })
      trackInvoiceEditor('Column visibility changed', { selectedColumns })
    },
    [setValue, visibleColumns, trackInvoiceEditor],
  )

  return (
    <RadioGroup disabled={isLoading} onChange={handleTotalPriceRadioClick} value={value}>
      <InvoiceColumnsVisibilitySettingsSection>
        <Radio value={TotalPriceColumnSetting.NetTotal}>{getHeaderLabel('netTotal')}</Radio>
        <Radio value={TotalPriceColumnSetting.GrossTotal}>{getHeaderLabel('total')}</Radio>
        <Radio value={TotalPriceColumnSetting.Both}>
          {t('external_invoices.editor.form.field.invoice_lines.column_visibility_settings.columns.total_price.both')}
        </Radio>
      </InvoiceColumnsVisibilitySettingsSection>
    </RadioGroup>
  )
}
